import React, { createRef } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import GetAppIcon from '@material-ui/icons/GetApp';
import config from 'shared/config/config';
import {
  Player,
  ControlBar,
  CurrentTimeDisplay,
  TimeDivider,
  DurationDisplay,
  ProgressControl,
  VolumeMenuButton,
  BigPlayButton,
  FullscreenToggle
} from 'video-react';
import 'video-react/dist/video-react.css';

import {
  setPhotoSwipeOpen,
  setPhotoSwipeMomentId
} from 'shared/actions/photoSwipeActions';

import {
  selectItem,
  unselectItem
} from 'shared/actions/selectionActions';

const styles = theme => ({
  zoom: {
    position: 'absolute',
    zIndex: 1,
    right: 0,
    bottom: 0,
    fontSize: '3rem',
    textShadow: '1px 1px 3px rgba(0, 0, 0, 0.3)',
    color: '#fff'
  },
  waypoint: {
    //position: 'relative'
    position: 'absolute',
    left: 0,
    top: '-10vh'
  },
  placeholder: {
    position: 'absolute',
    top: '2px',
    left: '2px',
    right: '2px',
    bottom: '2px',
    background: '#e6e6e6'
  }
});

class GalleryImage extends React.Component {
  constructor(props) {
    super(props);

    const { visible, resolution, attrs: { photo } } = props;

    this.ref = createRef();
    this.playerRef = createRef();

    this.state = {
      rendered: !!visible,
      visible: false,
      resolution: resolution
    }

    window.galleryImages = window.galleryImages || {};
    window.galleryImages[photo.moment_id] = this;
  }

  shouldComponentUpdate(newProps, newState) {
    const { attrs: { photo } } = this.props;

    return newProps.selectable !== this.props.selectable ||
      newState.rendered !== this.state.rendered ||
      newState.visible !== this.state.visible ||
      newState.resolution !== this.state.resolution ||
      (
        newProps.selectionItems &&
        newProps.selectionItems[photo.id] !== this.props.selectionItems[photo.id]
      ) ||
      newProps.attrs.photo.moment_id !== photo.moment_id;
  }

  componentDidUpdate() {
    const { attrs: { photo } } = this.props;
    window.galleryImages[photo.moment_id] = this;
  }

  onTap() {
    if (this.props.selectable) {
      this.toggleSelected();
    } else {
      this.viewImage();
    }
  }

  isSelected() {
    const { selectionItems, attrs: { photo } } = this.props;
    return selectionItems && selectionItems[photo.id];
  }

  toggleSelected() {
    const { selectItem, unselectItem, attrs: { photo } } = this.props;
    if (this.isSelected()) {
      unselectItem(photo);
    } else {
      selectItem(photo);
    }
  }

  viewImage() {
    const scrollNode = this.props.scrollNode && this.props.scrollNode.current;
    if (scrollNode) {
      window.savedScroll = scrollNode.scrollTop;
    }

    const { storyId, attrs: { photo } } = this.props;
    const pos = typeof photo.position !== 'undefined' ? photo.position : photo.index;
    const isVideo = photo.type && photo.type.includes("video/");
    if (isVideo) {
      return;
    }

    this.props.setPhotoSwipeMomentId(photo.moment_id, this.props.trackerOpts);
    this.props.setPhotoSwipeOpen(storyId);
  }

  show() {
    if (this.ref.current) {
      // trigger reflow for transition
      void this.ref.current.offsetWidth;
    }

    if (!this.state.visible) {
      this.setState({ visible: true });
    }

    if (this.props.progressive && this.state.resolution !== this.props.maxResolution) {
      this.setState({ resolution: this.props.maxResolution });
    }
  }

  handleFullscreen() {
    const videoEl = this.playerRef.current?.video?.video;
    if (videoEl.webkitEnterFullscreen) {
      videoEl.webkitEnterFullscreen();
    }
    // Other browsers
    else if (videoEl.requestFullscreen) {
      videoEl.requestFullscreen();
    }
  }

  handleDownload(moment) {
    const link = document.createElement("a");
    link.href = moment.download_url;
    link.download = 'video.mp4';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  renderSelectableImage(src) {
    const { attrs, attrs: { photo }, isMobile } = this.props;
    const selected = this.isSelected();
    const isVideo = photo.type && photo.type.includes("video/");

    const Checkmark = ({ selected }) => (
      <div
        style={
          selected
            ? { left: "4px", top: "4px", position: "absolute", zIndex: "1" }
            : { display: "none" }
        }
      >
        <svg
          style={{ fill: "white", position: "absolute" }}
          width="24px"
          height="24px"
        >
          <circle cx="12.5" cy="12.2" r="8.292" />
        </svg>
        <svg
          style={{ fill: config.pwa_checkmark_color, position: "absolute" }}
          width="24px"
          height="24px"
        >
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
        </svg>
      </div>
    );

    const imgStyle = {
      width: '100%',
      height: '100%',
      transition: "transform .135s cubic-bezier(0.0,0.0,0.2,1),opacity linear .15s",
      border: '2px solid #fff'
    };

    let cont = {
      width: '100%',
      height: '100%',
      cursor: "pointer",
      overflow: "hidden",
      position: "relative",
      opacity: 1
    };

    const sw = 200;
    const sh = attrs.photo.height / attrs.photo.width * sw;
    const sx = (100 - (30 / sw) * 100) / 100;
    const sy = (100 - (30 / sh) * 100) / 100;

    let selectedImgStyle = {
      transform: `translateZ(0px) scale3d(${sx}, ${sy}, 1)`
    }

    if (attrs.direction === "column") {
      cont.position = "absolute";
      cont.left = attrs.left;
      cont.top = attrs.top;
    }

    // show image sequence number on hover
    let sequenceNumber = attrs.photo.position + 1;

    let img = (
      isVideo ?
        <div
          style={
            selected ? { ...imgStyle, ...selectedImgStyle } : { ...imgStyle }
          }
        >
          <Player playsInline src={attrs.photo.print_url}>
            <BigPlayButton disabled={false} />
            <ControlBar>
              <CurrentTimeDisplay disabled={isMobile} />
              <TimeDivider disabled={isMobile} />
              <DurationDisplay disabled={isMobile} />
              <ProgressControl disabled={isMobile} />
              <VolumeMenuButton disabled={isMobile} />
            </ControlBar>
          </Player>
        </div> :
        <img
          style={
            selected ? { ...imgStyle, ...selectedImgStyle } : { ...imgStyle }
          }
          src={src}
          onClick={this.toggleSelected.bind(this)}
          title={sequenceNumber}
        />
    );

    let checkmark = (<Checkmark selected={selected} />);

    return (
      <div
        style={{ ...cont }}
      >
        {checkmark}
        {img}
      </div>
    );
  }

  render() {
    const { classes, attrs, isMobile } = this.props;
    const { rendered, visible, resolution } = this.state;
    const isVideo = attrs.photo.type && attrs.photo.type.includes("video/");

    const imgStyle = {
      position: 'relative',
      width: '100%',
      height: '100%',
      border: `2px solid ${config.pwa_story_title_bg_color}`,
      transition: 'opacity 0.5s',
      opacity: visible ? 1 : 0
    };

    let cont = {
      width: '100%',
      height: '100%',
      cursor: "pointer",
      overflow: "hidden",
      position: "relative"
    };

    if (attrs.direction === "column") {
      cont.position = "absolute";
      cont.left = attrs.left;
      cont.top = attrs.top;
    }

    let img = null;
    if (rendered) {
      var src;
      switch (resolution) {
        case 0:
          src = attrs.photo.thumb_url;
          break;

        case 1:
          src = attrs.photo.thumb_url_large;
          break

        case 2:
          src = attrs.photo.preview_url_small;
          break;

        case 3:
          src = attrs.photo.preview_url;
      }

      if (isVideo) {
        src = attrs.photo.preview_url;
        this.show();
      }

      if (this.props.selectable) {
        img = this.renderSelectableImage(src);
      } else {
        // show image sequence number on hover
        let sequenceNumber = attrs.photo.position + 1;

        img = (
          isVideo ?
            <div style={imgStyle}>
              <Player ref={this.playerRef} playsInline src={attrs.photo.print_url}>
                <BigPlayButton disabled={false} />
                <ControlBar>
                  <CurrentTimeDisplay disabled={isMobile} />
                  <TimeDivider disabled={isMobile} />
                  <DurationDisplay disabled={isMobile} />
                  <ProgressControl disabled={isMobile} />
                  <VolumeMenuButton disabled={isMobile} />
                  <FullscreenToggle disabled={isMobile} />
                  {
                    isMobile &&
                    <button
                      className="video-react-control video-react-button"
                      onClick={this.handleFullscreen.bind(this)}
                    >
                      <FullscreenIcon/>
                    </button>
                  }
                  <button
                    onClick={this.handleDownload.bind(this, attrs.photo)}
                    style={{
                      background: "transparent",
                      color: "#fff",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "14px",
                      marginLeft: "10px",
                    }}
                  >
                    <GetAppIcon/>
                  </button>
                </ControlBar>
              </Player>
            </div> :
            <img
              ref={this.ref}
              style={imgStyle}
              src={src}
              onLoad={this.show.bind(this)}
              title={sequenceNumber}
            />
        );
      }
    }

    var style;
    if (this.props.style) {
      style = {
        ...this.props.style,
        ...cont
      }
    } else {
      style = {
        borderRadius: '3px',
        margin: '2px',
        ...cont
      }
    }

    return (
      <div style={style} onClick={this.onTap.bind(this)}>
        <div className={classes.placeholder} />
        {img}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  selectable: state.selectionReducer && state.selectionReducer.selection.enabled,
  selectionItems: state.selectionReducer && state.selectionReducer.selection.items
});

export default connect(mapStateToProps, { setPhotoSwipeOpen, setPhotoSwipeMomentId, selectItem, unselectItem })(withStyles(styles)(GalleryImage));
